import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import client, { formatApiError } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Globe, BookTemplate, Copy, ArrowRight, ShieldCheck } from "lucide-react";

export default function GlobalResourcesPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [apiDefs, setApiDefs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [forking, setForking] = useState(null);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    const [tRes, aRes] = await Promise.all([
      client.get("/templates/").catch(() => ({ data: [] })),
      client.get("/api-defs/").catch(() => ({ data: [] })),
    ]);
    setTemplates(tRes.data.filter(t => t.is_global));
    setApiDefs(aRes.data.filter(d => d.is_global));
    setLoading(false);
  };

  const forkTemplate = async (t) => {
    setForking(t._id);
    try {
      const res = await client.post(`/templates/${t._id}/fork`, { name: `${t.name} (My Copy)` });
      navigate(`/templates`);
    } catch (e) {
      alert(formatApiError(e));
    } finally { setForking(null); }
  };

  const isSuperAdmin = user?.role === "super_admin";
  const methodColor = { GET: "#059669", POST: "#2563EB", PUT: "#D97706", PATCH: "#7C3AED", DELETE: "#DC2626" };

  return (
    <div className="p-6 max-w-5xl mx-auto fade-in">
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-1">
          <ShieldCheck size={22} className="text-[#006080]" />
          <h1 className="text-2xl font-bold text-gray-900">Global Resources</h1>
        </div>
        <p className="text-gray-500 text-sm">Templates and API definitions shared across all companies. Fork them to create your own customizable copy.</p>
      </div>
      <Tabs defaultValue="templates">
        <TabsList className="h-auto mb-4">
          <TabsTrigger value="templates">Global Templates ({templates.length})</TabsTrigger>
          <TabsTrigger value="apis">Global API Definitions ({apiDefs.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="templates">
          {loading ? <div className="space-y-2">{[1,2].map(i => <div key={i} className="h-16 bg-white border border-gray-200 rounded-lg animate-pulse" />)}</div>
          : templates.length === 0 ? (
            <div className="text-center py-12 bg-white border border-gray-200 rounded-lg">
              <BookTemplate size={36} className="mx-auto text-gray-200 mb-3" />
              <p className="text-gray-400">No global templates yet</p>
              {isSuperAdmin && <p className="text-xs text-gray-400 mt-1">Save a template with "Make Global" checked to share it here</p>}
            </div>
          ) : (
            <div className="space-y-3">
              {templates.map(t => (
                <div key={t._id} className="bg-white border border-[#006080]/20 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 bg-[#e6f2f5] rounded-md flex items-center justify-center flex-shrink-0"><BookTemplate size={18} className="text-[#006080]" /></div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-gray-900">{t.name}</p>
                        <Badge className="text-xs bg-[#006080] text-white">Global</Badge>
                        {t.forked_from && <Badge variant="secondary" className="text-xs">Forked</Badge>}
                      </div>
                      {t.description && <p className="text-xs text-gray-400 mt-0.5">{t.description}</p>}
                      <div className="flex gap-2 mt-1.5">
                        <Badge variant="secondary" className="text-xs">{t.column_schema?.length || 0} cols</Badge>
                        <Badge variant="secondary" className="text-xs">{t.pipeline?.length || 0} transforms</Badge>
                        <Badge variant="secondary" className="text-xs">{t.api_definitions?.length || 0} APIs</Badge>
                      </div>
                    </div>
                    <div className="flex gap-2 flex-shrink-0">
                      {!isSuperAdmin && (
                        <Button size="sm" className="h-7 px-3 text-xs bg-[#006080] text-white hover:bg-[#004d66]" onClick={() => forkTemplate(t)} disabled={forking === t._id}>
                          <Copy size={12} className="mr-1" />{forking === t._id ? "Forking..." : "Fork"}
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
        <TabsContent value="apis">
          {loading ? <div className="space-y-2">{[1,2].map(i => <div key={i} className="h-16 bg-white border border-gray-200 rounded-lg animate-pulse" />)}</div>
          : apiDefs.length === 0 ? (
            <div className="text-center py-12 bg-white border border-gray-200 rounded-lg">
              <Globe size={36} className="mx-auto text-gray-200 mb-3" />
              <p className="text-gray-400">No global API definitions yet</p>
              {isSuperAdmin && <p className="text-xs text-gray-400 mt-1">Mark an API definition as Global in the API Definitions page</p>}
            </div>
          ) : (
            <div className="space-y-3">
              {apiDefs.map(d => (
                <div key={d._id} className="bg-white border border-[#006080]/20 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <div className="px-2 py-0.5 rounded text-xs font-bold text-white flex-shrink-0 mt-0.5" style={{ background: methodColor[d.method] || "#4B5563" }}>{d.method}</div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-gray-900">{d.name}</p>
                        <Badge className="text-xs bg-[#006080] text-white">Global</Badge>
                      </div>
                      <p className="text-xs text-gray-400 font-mono truncate mt-0.5">{d.base_url}{d.path}</p>
                    </div>
                    <Button size="sm" variant="outline" className="h-7 px-3 text-xs" onClick={() => navigate("/definitions")}>
                      Use <ArrowRight size={11} className="ml-1" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
